import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api } from './api';

interface AuthState {
  username: string | null;
  loading: boolean;
  provisioned: boolean;
  ephemeralSessionSecret: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Called by the API layer when a request comes back 401. */
  handleUnauthorised: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [provisioned, setProvisioned] = useState(true);
  const [ephemeralSessionSecret, setEphemeral] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const status = await api.authStatus();
        if (!cancelled) {
          setProvisioned(status.provisioned);
          setEphemeral(status.ephemeralSessionSecret);
        }
      } catch {
        // Status is advisory; a failure here should not block the login form.
      }

      try {
        const me = await api.me();
        if (!cancelled) setUsername(me.username);
      } catch {
        if (!cancelled) setUsername(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (name: string, password: string) => {
    const result = await api.login(name, password);
    setUsername(result.username);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUsername(null);
    }
  }, []);

  const handleUnauthorised = useCallback(() => {
    setUsername(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      username,
      loading,
      provisioned,
      ephemeralSessionSecret,
      signIn,
      signOut,
      handleUnauthorised,
    }),
    [username, loading, provisioned, ephemeralSessionSecret, signIn, signOut, handleUnauthorised],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/**
 * Shared loader for pages. Refetches on an interval and surfaces a 401 to the
 * auth context so an expired session bounces the user to the login screen
 * instead of showing a broken page.
 */
export function usePolling<T>(
  loader: () => Promise<T>,
  intervalMs: number | null,
  deps: unknown[] = [],
): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const { handleUnauthorised } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        const result = await loader();
        if (cancelled) return;
        setData(result);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.isUnauthorised) {
          handleUnauthorised();
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();

    if (intervalMs === null) {
      return () => {
        cancelled = true;
      };
    }

    const timer = setInterval(() => void run(), intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, tick, ...deps]);

  return { data, error, loading, reload };
}
